// ─── AR Reconciliation – Access & Hospital helpers ─────────────────────────────
// Centralised so both the Header tab and the page route-guard stay in sync.

// Role that grants access to AR Reconciliation.
export const AR_ACCESS_ROLE = 'ar_dash';

// Hardcoded hospitals list for specific users
export const HARDCODED_HOSPITALS = [
  { name: "Synergy Global Care Hospital", id: "VdM6OVOLmU" },
  { name: "Freya Hospital - Ranchi", id: "YCKgSChkaO" },
  { name: "Neelachal Hospital", id: "G2M7fkVimO" },
  { name: "Ridansh Hospital", id: "F2pcLZYBoL" },
  { name: "Dr. OP Chaudhary Hospital", id: "mUmBRQlKqS" },
  { name: "Panacea Hospital", id: "QWMhJTZ1Em" },
  { name: "Medicover Hospital - Secunderabad", id: "uRqJv7KATH" },
  { name: "Medicover Navi Mumbai", id: "BQQX5Xsik7" },
  { name: "Medicover MVP", id: "EaBPM9Twsj" },
  { name: "Eye Mantra Hospital", id: "GDunfdR4GA" },
  { name: "Aadhya9 Multi-Speciality Hospital", id: "XqxtbB52QQ" },
  { name: "Amerix Hospital", id: "sArtFUVwY8" },
  { name: "SMC Hospital - Raipur", id: "fgD50CCK3N" },
  { name: "Capital Hospital - Vijayawada", id: "ZNh66PHWII" },
  { name: "Kare Partner Hospital - Chandigarh", id: "RCo2M2alh4" },
  { name: "KARE Partner - Punchkula", id: "oUnE0gnxXe" },
  { name: "Usthi Hospital", id: "etpeDhvPTq" },
  { name: "Holy Promise", id: "IQARXhFVYv" }
];

export const HARDCODED_HOSPITAL_MAP = HARDCODED_HOSPITALS.reduce((acc, curr) => {
  acc[curr.id] = curr.name;
  return acc;
}, {});

const HARDCODED_HOSPITAL_IDS_LIST = HARDCODED_HOSPITALS.map((h) => h.id);

// TEMP: users (matched by email OR username) that should always get AR access +
// hardcoded hospitals until their real roles / HospitalIds are provisioned.
// Remove once the backend is ready.
const TEMP_WHITELIST_EMAILS = [
  'sunny.sen@pristyncare.com',
  'rohit.kumar6@pristyncare.com',
  'md.arif@pristyncare.com',
];

const TEMP_WHITELIST_USERNAMES = [
  'sunny.sen',
  'rohit.kumar6',
  'md.arif',
];

const TEMP_FALLBACK_HOSPITAL_IDS = ['GDunfdR4GA', 'X8nBAtLR3H', 'Nv5CAnnU35'];

const USER_HARDCODED_HOSPITALS = {
  'sunny.sen@pristyncare.com': ['GDunfdR4GA', 'X8nBAtLR3H', 'Nv5CAnnU35'],
  'sunny.sen': ['GDunfdR4GA', 'X8nBAtLR3H', 'Nv5CAnnU35'],
  'rohit.kumar6@pristyncare.com': HARDCODED_HOSPITAL_IDS_LIST,
  'rohit.kumar6': HARDCODED_HOSPITAL_IDS_LIST,
  'md.arif@pristyncare.com': HARDCODED_HOSPITAL_IDS_LIST,
  'md.arif': HARDCODED_HOSPITAL_IDS_LIST,
};

const normalize = (v) => (v == null ? '' : String(v).trim().toLowerCase());

const getUserEmail = (user) =>
  normalize(user?.email || user?.emailId || user?.Email || user?.email_id);

const getUserName = (user) =>
  normalize(user?.username || user?.user_name || user?.userName || user?.name || user?.userId);

const isWhitelisted = (user) =>
  TEMP_WHITELIST_EMAILS.includes(getUserEmail(user)) ||
  TEMP_WHITELIST_USERNAMES.includes(getUserName(user));

/**
 * True when the user may access AR Reconciliation.
 * Checks roles.role and roles.otherRoles (case-insensitive) for `ar_dash`.
 */
export function hasArAccess(user) {
  if (!user) return false;

  // TEMP whitelist bypass (by email / username)
  if (isWhitelisted(user)) return true;

  const target = AR_ACCESS_ROLE.toLowerCase();
  const role = normalize(user?.roles?.role);
  if (role === target) return true;

  const otherRoles = user?.roles?.otherRoles;
  if (Array.isArray(otherRoles)) {
    return otherRoles.some((r) => normalize(r) === target);
  }
  return false;
}

/**
 * Returns the user's hospital ids as a clean array of strings.
 * `HospitalIds` may be a single string or an array. Falls back to the TEMP
 * hardcoded id for whitelisted users when nothing is assigned.
 */
export function getUserHospitalIds(user) {
  const email = getUserEmail(user);
  const username = getUserName(user);

  // If user has specific hardcoded hospital mapping
  if (USER_HARDCODED_HOSPITALS[email]) {
    return [...USER_HARDCODED_HOSPITALS[email]];
  }
  if (USER_HARDCODED_HOSPITALS[username]) {
    return [...USER_HARDCODED_HOSPITALS[username]];
  }

  const raw = user?.HospitalIds;
  let ids = [];

  if (Array.isArray(raw)) {
    ids = raw;
  } else if (raw != null && String(raw).trim() !== '') {
    ids = [raw];
  }

  ids = ids.map((id) => String(id).trim()).filter(Boolean);

  // TEMP: give whitelisted users (by email) a working hospital id if none assigned
  if (ids.length === 0 && isWhitelisted(user)) {
    return [...TEMP_FALLBACK_HOSPITAL_IDS];
  }

  return ids;
}
